import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flag, Check, Loader2, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDur } from "@/lib/ui";

const STATE_META = {
  GREEN: { label: "CRÉNEAU PARFAIT", cls: "text-emerald-400", ring: "border-emerald-500/40 bg-emerald-500/10" },
  ORANGE: { label: "CRÉNEAU SERRÉ", cls: "text-amber-400", ring: "border-amber-500/40 bg-amber-500/10" },
  NO_GO: { label: "SEMAINE CHARGÉE", cls: "text-rose-400", ring: "border-rose-500/40 bg-rose-500/10" },
};

export default function GolfWidget({ compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blocking, setBlocking] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/golf");
      setData(res.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const block = async (w) => {
    setBlocking(w.date);
    try {
      await api.post("/golf/block", { start_iso: w.start_iso, end_iso: w.end_iso });
      toast.success(`Créneau golf bloqué — ${w.day} ${w.start}`);
    } catch {
      toast.error("Impossible de bloquer le créneau");
    } finally { setBlocking(null); }
  };

  const overall = data?.overall || "NO_GO";
  const meta = STATE_META[overall];

  return (
    <div data-testid="golf-widget" className="h-full flex flex-col">
      <div className="flex items-center gap-2.5">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${meta.ring} border`}>
          <Flag className={`w-4 h-4 ${meta.cls}`} />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Puis-je jouer au golf ?</div>
          <div className={`font-display text-sm font-semibold ${meta.cls}`}>{meta.label}</div>
        </div>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : overall === "NO_GO" ? (
        <div className="mt-5 text-sm text-muted-foreground">
          Aucune plage de 3h+ disponible cette semaine sans déplacer un rendez-vous prioritaire.
        </div>
      ) : (
        <div className="mt-4 space-y-2.5 flex-1">
          {(compact ? data.windows.slice(0, 2) : data.windows).map((w) => {
            const wm = STATE_META[w.status];
            return (
              <motion.div key={w.date} whileHover={{ y: -2 }}
                data-testid={`golf-window-${w.date}`}
                className={`rounded-xl border p-3.5 ${wm.ring}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-foreground">{w.day}</div>
                    <div className="font-mono text-xs text-muted-foreground">{w.start} — {w.end} · {fmtDur(w.duration_minutes)}</div>
                  </div>
                  <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full ${wm.ring} ${wm.cls}`}>{w.status}</span>
                </div>
                {!compact && <div className="text-xs text-muted-foreground mt-2">{w.reason}</div>}
                <button
                  data-testid={`golf-block-${w.date}`}
                  onClick={() => block(w)}
                  disabled={blocking === w.date}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 text-white text-xs font-semibold py-2 transition-all hover:bg-emerald-600 disabled:opacity-60">
                  {blocking === w.date ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                    w.status === "ORANGE" ? <><CalendarPlus className="w-3.5 h-3.5" /> BLOQUER (9 TROUS)</> :
                    <><Check className="w-3.5 h-3.5" /> BLOQUER CE CRÉNEAU</>}
                </button>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
